import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useCateringFollowup — drives CateringFollowupSection on a single
// catering inquiry detail view. Per-inquiry instance: pass inquiryId.
//
// Patterns mirror useScheduleSuggestions exactly:
//   - cache-first read against ai_briefs (tab_key='catering',
//     sub_key=`followup-${inquiryId}`)
//   - lazy generate via the generate-catering-followup edge fn
//   - synchronous inFlightRef double-fire guard
//   - cooldown timer on generate() — 5000ms here (vs 30s on schedule
//     suggestions). Follow-up drafts are cheaper to re-spin and the
//     manager often wants to re-roll the tone, so the UI cooldown is
//     brief — the server-side 30s rate limit still gates real spend.
//   - 24h cache window (vs 6h on schedule suggestions). A followup
//     draft stays useful all day; we'd rather show yesterday's draft
//     than auto-burn budget on every open.

const TAB_KEY = 'catering'
const FN_NAME = 'generate-catering-followup'
const CACHE_WINDOW_HOURS = 24
const COOLDOWN_MS = 5_000

export function useCateringFollowup(inquiryId, restaurantId) {
  // Phases: idle | generating | ready | empty | error | cached
  // - 'idle'      — no cache row within 24h; waiting on user
  // - 'generating' → edge fn in flight
  // - 'cached'    — cache hit, payload populated
  // - 'ready'     — fresh generate() succeeded, payload populated
  // - 'empty'     — payload._empty (tiny-data nudge OR timeout retryable)
  // - 'error'     — edge fn returned a structured error
  //
  // restaurantId is the INQUIRY's own restaurant_id, passed by the caller,
  // so the ai_briefs cache read is scoped to the unit that owns the inquiry
  // (the same unit the edge fn writes the brief under). The old "first
  // active restaurant" lookup scoped the cache to the operator's FIRST unit,
  // breaking cache hits — and the edge fn's matching primary-unit filter
  // broke generation outright — for inquiries on any non-primary unit.
  const [phase, setPhase]               = useState('idle')
  const [payload, setPayload]           = useState(null)
  const [generatedAt, setGeneratedAt]   = useState(null)
  const [error, setError]               = useState(null)
  const [cooldownRemainingMs, setCool]  = useState(0)

  // Synchronous double-fire guard.
  const inFlightRef = useRef(false)
  const cooldownTickRef = useRef(null)
  const cooldownEndsAtRef = useRef(0)

  // Reset state when the inquiry changes so opening a different
  // inquiry's modal doesn't briefly flash the previous draft.
  useEffect(() => {
    setPhase('idle')
    setPayload(null)
    setGeneratedAt(null)
    setError(null)
  }, [inquiryId])

  // Cache lookup on mount (and when inquiryId or restaurantId resolves).
  // Cache HIT within 24h → 'cached' or 'empty'. Cache MISS → 'idle'.
  useEffect(() => {
    if (!restaurantId || !inquiryId) return
    const subKey = `followup-${inquiryId}`
    let cancelled = false
    const cutoff = new Date(Date.now() - CACHE_WINDOW_HOURS * 60 * 60 * 1000).toISOString()
    supabase
      .from('ai_briefs')
      .select('generated_at, payload')
      .eq('restaurant_id', restaurantId)
      .eq('tab_key', TAB_KEY)
      .eq('sub_key', subKey)
      .gte('generated_at', cutoff)
      .order('generated_at', { ascending: false })
      .limit(1)
      .then(({ data, error: e }) => {
        if (cancelled) return
        if (e) {
          // eslint-disable-next-line no-console
          console.warn('[useCateringFollowup] cache read failed', e)
          return
        }
        const row = (data && data[0]) || null
        if (!row) return
        setPayload(row.payload || null)
        setGeneratedAt(row.generated_at)
        if (row.payload && row.payload._empty) setPhase('empty')
        else setPhase('cached')
      })
    return () => { cancelled = true }
  }, [restaurantId, inquiryId])

  // Cooldown tick — same pattern as useScheduleSuggestions.
  const startCooldown = useCallback(() => {
    cooldownEndsAtRef.current = Date.now() + COOLDOWN_MS
    setCool(COOLDOWN_MS)
    if (cooldownTickRef.current) clearInterval(cooldownTickRef.current)
    cooldownTickRef.current = setInterval(() => {
      const remaining = Math.max(0, cooldownEndsAtRef.current - Date.now())
      setCool(remaining)
      if (remaining <= 0 && cooldownTickRef.current) {
        clearInterval(cooldownTickRef.current)
        cooldownTickRef.current = null
      }
    }, 1000)
  }, [])

  useEffect(() => {
    return () => {
      if (cooldownTickRef.current) {
        clearInterval(cooldownTickRef.current)
        cooldownTickRef.current = null
      }
    }
  }, [])

  const generate = useCallback(async (_force = false) => {
    if (!restaurantId || !inquiryId) return
    if (inFlightRef.current) return
    if (cooldownRemainingMs > 0) return

    inFlightRef.current = true
    setError(null)
    setPhase('generating')
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/${FN_NAME}`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ inquiry_id: inquiryId }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data?.error) {
        setPhase('error')
        setError(data.message || "We couldn't draft a follow-up right now.")
        return
      }
      setPayload(data || null)
      setGeneratedAt(new Date().toISOString())
      if (data?._empty) setPhase('empty')
      else setPhase('ready')
      startCooldown()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useCateringFollowup] edge fn call failed', e)
      // Deterministic calm fallback. Never the raw error per CLAUDE.md.
      setPhase('error')
      setError("We couldn't draft a follow-up right now.")
    } finally {
      inFlightRef.current = false
    }
  }, [restaurantId, inquiryId, cooldownRemainingMs, startCooldown])

  return useMemo(
    () => ({
      phase,
      payload,
      generatedAt,
      cooldownRemainingMs,
      generate,
      error,
    }),
    [phase, payload, generatedAt, cooldownRemainingMs, generate, error],
  )
}
